import React, { useState, useEffect, useRef, useMemo } from "react";
import Avatar from "react-avatar"
import Icon from "../../../../icons";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SupervisorSelect = ({
  input,
  meta,
  options = [],
  onScrollEnd,
  onSearch,
  isLoadingMore,
  selected,
  title,
  errorMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pickedOption, setPickedOption] = useState(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const selectedValue = (input && input.value) || selected;

  const selectedOption = useMemo(() => {
    const found = options.find((opt) => opt.Value === selectedValue);
    if (found) return found;
    if (pickedOption && pickedOption.Value === selectedValue) return pickedOption;
    return undefined;
  }, [options, selectedValue, pickedOption]);

  const orderedOptions = useMemo(() => {
    if (!selectedValue) return options;
    const rest = options.filter((opt) => opt.Value !== selectedValue);
    return selectedOption ? [selectedOption, ...rest] : options;
  }, [options, selectedValue, selectedOption]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (!onSearch) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      onSearch(value);
    }, 400);
  };

  const handleSelect = (option) => {
    if (input && input.onChange) input.onChange(option.Value);
    setPickedOption(option);
    setIsOpen(false);
    setSearchText("");
    if (onSearch) onSearch("");
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (onScrollEnd && scrollHeight - scrollTop - clientHeight < 50) {
      onScrollEnd();
    }
  };

  // `errorMessage` is passed in from a validator that's guaranteed to be
  // recomputed against the current field value on every render (unlike
  // redux-form's own `meta.error`, which can go stale after a change).
  const showError = meta && meta.touched && !!errorMessage;

  return (
    <div ref={containerRef} className="">
      <div
        className={`mailDropdown select ${showError ? "errorState" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="alignMiddle rowSpaceBetween flexGrow">
          {selectedOption ? (
            <span>{selectedOption.DisplayName}</span>
          ) : (
            <span className="placeholder">{title || "Select the Supervisor for this user."}</span>
          )}
          <Icon name="selectArrow" className="icon arrowDown" />
        </div>
      </div>
      {isOpen && (
        <div className="dropDown onClick alignMiddle flexGrow">
          <div className="dropDownMenu ownerPopup onClick animated fast">
            <div className="searchOwner">
              <div className="formGroup mrNone">
                <input
                  tabIndex="1"
                  className="line"
                  type="text"
                  placeholder="Search by Name, Job Title or Email"
                  value={searchText}
                  onChange={handleSearchChange}
                  autoFocus
                />
              </div>
            </div>
            <ul
              ref={listRef}
              onScroll={handleScroll}
              style={{ maxHeight: "260px", overflowY: "auto" }}
            >
              {orderedOptions.map((option) => {
                const isSelected = option.Value === selectedValue;
                return (
                  <li
                    key={option.Value}
                    className={`${isSelected ? "activeClass " : ""}userGroup `}
                    onClick={() => handleSelect(option)}
                  >
                    <div className="userPic userPicIcon">
                        <Avatar 
                          round={true}
                          size={"32"}
                          name={option.DisplayName}
                          maxInitials={2} 
                        />
                        {/* {getInitials(option.DisplayName)} */}
                    </div>
                    <div className="userInfo">
                      {option.DisplayName}
                    </div>
                    {isSelected && (
                      <span className="badges checked selectChecked green"></span>
                    )}
                  </li>
                );
              })}
              {isLoadingMore && (
                <li className="userGroup ">
                  <div className="userInfo">Loading more...</div>
                </li>
              )}
              {options.length === 0 && !isLoadingMore && (
                <li className="userGroup ">
                  <div className="userInfo">No matches found</div>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
      {showError && <span className="errorMsg">{errorMessage}</span>}
    </div>
  );
};

export default SupervisorSelect;
